/**
 * Flux Service - BFL (Black Forest Labs) API Integration via Vite Dev Proxy
 * Supports Flux 2 Pro and Flux 2 Flex models
 *
 * Architecture:
 *   Browser → /flux-proxy/* (Vite dev server) → api.bfl.ai/v1/*
 *   Vite proxy adds x-key header server-side using FLUX_REPLICATE_API_TOKEN
 *   so the token is never exposed to the browser.
 *
 * Setup:
 *   1. Add FLUX_REPLICATE_API_TOKEN=your_bfl_token in .env (only env var needed)
 *   2. Vite proxy is configured in vite.config.mjs
 *   Flux models always appear in the UI dropdown. Token validation happens server-side.
 *
 * BFL API Flow:
 *   1. POST /flux-proxy/{model-endpoint} → returns { polling_url }
 *   2. GET polling_url (repeat) → { status: "Pending" | "Ready" | "Error" }
 *   3. When "Ready" → { status: "Ready", result: { sample: "https://..." } }
 */

// Flux models are always available in the UI.
const FLUX_ENABLED = true;

// All environments use Supabase Edge Functions for Flux API
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

function getFluxApiBase() {
  return `${SUPABASE_URL}/functions/v1/flux-api`;
}

function getBflFetchBase() {
  return `${SUPABASE_URL}/functions/v1/bfl-fetch`;
}

async function getEdgeFunctionHeaders() {
  const { supabase } = await import('../lib/supabase');
  const { data: { session } } = await supabase.auth.getSession();
  return {
    'apikey': SUPABASE_ANON_KEY,
    'Authorization': `Bearer ${session?.access_token || SUPABASE_ANON_KEY}`,
  };
}

/**
 * Supported Flux model endpoints on BFL API
 */
const FLUX_MODELS = {
  "Flux Pro": "flux-2-pro",
  "Flux Flex": "flux-2-flex",
};

/**
 * Aspect ratio → width/height mapping for BFL API
 * BFL API uses width & height (multiples of 16, minimum 64px)
 * Target ~1 megapixel for good quality
 */
const ASPECT_RATIO_SIZES = {
  "1:1":  { width: 1024, height: 1024 },
  "4:3":  { width: 1184, height: 880 },
  "3:2":  { width: 1216, height: 816 },
  "16:9": { width: 1344, height: 768 },
  "21:9": { width: 1536, height: 656 },
  "2:3":  { width: 816, height: 1216 },
  "3:4":  { width: 880, height: 1184 },
  "9:16": { width: 768, height: 1344 },
  "9:21": { width: 656, height: 1536 },
};

/**
 * Maps UI aspect ratio strings to width/height
 */
function getImageDimensions(aspectRatio) {
  const mapping = {
    Auto: "1:1",
    "5:4": "4:3",
    "4:5": "3:4",
  };
  const key = mapping[aspectRatio] || aspectRatio;
  return ASPECT_RATIO_SIZES[key] || ASPECT_RATIO_SIZES["1:1"];
}

/**
 * Converts a blob URL or data URL to a base64 data URI string
 * BFL API accepts base64-encoded images
 */
async function imageUrlToBase64DataUri(imageUrl) {
  if (imageUrl?.startsWith("data:")) return imageUrl;

  const response = await fetch(imageUrl);
  const blob = await response?.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader?.result);
    reader.onerror = reject;
    reader?.readAsDataURL(blob);
  });
}

/**
 * Handles BFL API errors with user-friendly messages
 */
function handleFluxError(error) {
  const message = error?.message || "";

  if (
    message?.includes("Failed to fetch") ||
    message?.includes("NetworkError") ||
    message?.includes("CORS")
  ) {
    return {
      isInternal: true,
      message:
        "Cannot connect to BFL API. Please check your network connection and server configuration.",
    };
  }

  if (
    message?.includes("Invalid") ||
    message?.includes("Unauthorized") ||
    message?.includes("401") ||
    message?.includes("authentication")
  ) {
    return {
      isInternal: true,
      message:
        "Invalid BFL API token. Please check FLUX_REPLICATE_API_TOKEN in Supabase secrets.",
    };
  }

  if (message?.includes("rate limit") || message?.includes("429")) {
    return {
      isInternal: true,
      message: "BFL API rate limit exceeded. Please try again later.",
    };
  }

  if (
    message?.includes("insufficient") ||
    message?.includes("payment") ||
    message?.includes("402") ||
    message?.includes("credits")
  ) {
    return {
      isInternal: true,
      message:
        "Insufficient BFL API credits. Please check your account at bfl.ai.",
    };
  }

  return {
    isInternal: false,
    message: message || "An unexpected error occurred with Flux generation.",
  };
}

/**
 * Submits a generation request to BFL API via /flux-api middleware
 * Returns the polling URL for checking status
 *
 * POST /flux-api/{model-endpoint} → POST https://api.bfl.ai/v1/{model-endpoint}
 */
async function submitGeneration(modelEndpoint, requestBody) {
  const edgeHeaders = await getEdgeFunctionHeaders();
  const url = `${getFluxApiBase()}?endpoint=${encodeURIComponent(modelEndpoint)}`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...edgeHeaders,
    },
    body: JSON.stringify(requestBody),
  });

  const data = await response?.json();

  if (!response?.ok) {
    throw new Error(
      data?.detail || data?.message || data?.error || `BFL API error: ${response?.status}`
    );
  }

  if (!data?.polling_url) {
    throw new Error("BFL API did not return a polling URL.");
  }

  return data.polling_url;
}

/**
 * Fetches a remote URL through the server-side /bfl-fetch proxy
 * This avoids CORS issues — works for any BFL domain or Azure blob URL
 */
async function serverFetch(targetUrl) {
  const edgeHeaders = await getEdgeFunctionHeaders();
  return fetch(`${getBflFetchBase()}?url=${encodeURIComponent(targetUrl)}`, {
    headers: edgeHeaders,
  });
}

/**
 * Polls BFL API for generation result via /bfl-fetch server proxy
 * BFL returns polling_url on various regional domains (api.eu2.bfl.ai, etc.)
 * We pass the full URL to /bfl-fetch which fetches it server-side with x-key
 *
 * Response when ready: { status: "Ready", result: { sample: "https://...", seed: 123 } }
 */
async function pollForResult(pollingUrl, signal) {
  const maxAttempts = 120; // ~4 minutes at 2s intervals
  let attempts = 0;

  while (attempts < maxAttempts) {
    if (signal?.aborted) {
      throw new Error("Image generation was cancelled.");
    }

    const response = await serverFetch(pollingUrl);
    const data = await response?.json();

    if (data?.status === "Ready") {
      return data;
    }

    if (data?.status === "Error") {
      throw new Error(
        data?.error || data?.message || "BFL image generation failed. Please try again."
      );
    }

    // Status is "Pending" or other — wait and retry
    await new Promise((r) => setTimeout(r, 2000));
    attempts++;
  }

  throw new Error("Flux image generation timed out. Please try again.");
}

/**
 * Downloads an image URL through server proxy and converts to base64 data URL
 * BFL result URLs are on Azure Blob (CORS-blocked) and expire in 10 minutes
 * We route through /bfl-fetch to avoid CORS and download immediately
 */
async function fetchImageAsBase64(url) {
  const response = await serverFetch(url);
  const blob = await response?.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader?.result);
    reader.onerror = reject;
    reader?.readAsDataURL(blob);
  });
}

/**
 * Builds a rich text prompt for Flux from moodboard inputs
 * Since Flux is primarily text-to-image, we compose a detailed prompt
 * incorporating room context
 */
function buildFluxPrompt({
  userPromptText = "",
  additionalPromptText = "",
  roomType = "",
  selectedAngleShot = "",
  selectedRoomSize = null,
  hasReference = false,
  hasMoodboard = false,
}) {
  const parts = [];

  parts.push(
    "Generate a hyper-realistic, photorealistic interior design visualization."
  );

  if (roomType && roomType !== "--") {
    parts.push(`Room type: ${roomType}.`);
  }

  if (selectedRoomSize) {
    const sizeMap = {
      small: "small room (≤ 12 m²)",
      medium: "medium room (13–25 m²)",
      large: "large room (26–40 m²)",
      "extra-large": "extra-large room (40+ m²)",
    };
    parts.push(`Room size: ${sizeMap[selectedRoomSize] || selectedRoomSize}.`);
  }

  if (selectedAngleShot && selectedAngleShot !== "--") {
    parts.push(`Camera angle: ${selectedAngleShot}.`);
  }

  if (hasReference) {
    parts.push(
      "Use the reference image(s) as spatial and architectural guide for the room layout."
    );
  }

  if (hasMoodboard) {
    parts.push(
      "Apply the design DNA from the moodboard images: match the exact colours, materials, textures, furniture styles, and atmosphere shown."
    );
  }

  if (userPromptText?.trim()) {
    parts.push(userPromptText.trim());
  }

  if (additionalPromptText?.trim()) {
    parts.push(additionalPromptText.trim());
  }

  parts.push(
    "Professional interior photography quality, realistic lighting and shadows, magazine editorial standard."
  );

  return parts.join(" ");
}

/**
 * Builds a rich text prompt for Flux specifically for Site Concept generation
 * Incorporates the same system prompt logic and generation settings as the Gemini path
 * so that Flux receives identical context — only the model differs.
 */
function buildFluxSiteConceptPrompt({
  toolSystemPrompt = "",
  generationSettings = {},
  promptText = "",
  commonAngleView = "frontal",
  selectedAngleShot = "--",
  hasSiteImages = false,
  hasReferenceImages = false,
  hasLibraryAssets = false,
}) {
  const parts = [];

  // Base instruction (same as Gemini path)
  parts.push(
    "Generate exactly what the prompt describes. Integrate the reference building onto the reference site seamlessly. " +
    "The final composition must be a unified, photorealistic image where the building appears naturally placed on the site."
  );

  // Include the full site concept system prompt
  if (toolSystemPrompt?.trim()) {
    parts.push(toolSystemPrompt.trim());
  }

  // Generation settings — same labels as Gemini path
  if (generationSettings?.floors && generationSettings.floors !== '--' && generationSettings.floors !== '') {
    parts.push(`NUMBER OF FLOORS: ${generationSettings.floors}.`);
  }

  if (generationSettings?.lighting && generationSettings.lighting !== '--' && generationSettings.lighting !== '') {
    parts.push(`LIGHTING CONDITION: ${generationSettings.lighting}.`);
  }

  if (generationSettings?.buildingType && generationSettings.buildingType !== '--' && generationSettings.buildingType !== '') {
    parts.push(`BUILDING TYPE: ${generationSettings.buildingType}.`);
  }

  if (generationSettings?.surrounding && generationSettings.surrounding !== '--' && generationSettings.surrounding !== '') {
    parts.push(`SURROUNDING CONTEXT: ${generationSettings.surrounding}.`);
  }

  if (generationSettings?.season && generationSettings.season !== '--' && generationSettings.season !== '') {
    parts.push(`SEASON: ${generationSettings.season}.`);
  }

  if (generationSettings?.sky && generationSettings.sky !== '--' && generationSettings.sky !== '') {
    parts.push(`SKY CONDITION: ${generationSettings.sky}.`);
  }

  if (promptText?.trim()) {
    parts.push(`USER PROMPT TEXT: ${promptText.trim()}`);
  }

  if (commonAngleView && commonAngleView !== '--') {
    const angleDesc = commonAngleView === 'topdown'
      ? 'topdown — ORTHOGRAPHIC TOP-DOWN: camera directly above at 90 degrees looking straight down. Only rooftops and ground visible. NO facades, NO walls, NO vertical surfaces.'
      : 'frontal — street-level perspective at eye height (1.5–1.7m), facing the building front facade';
    parts.push(`COMMON ANGLE VIEW: ${angleDesc}`);
  }

  if (selectedAngleShot && selectedAngleShot !== '--' && selectedAngleShot !== 'Angle view') {
    const angleShotDescriptions = {
      'Front View': 'Front View — camera at eye level, directly facing the main facade',
      'Side View': 'Side View — camera at eye level, perpendicular to the front facade, showing the side elevation',
      'Top View': 'Top View — TRUE ORTHOGRAPHIC TOP-DOWN: camera directly overhead at exactly 90 degrees looking straight down. ONLY rooftops, roads, landscaping visible. NO facades, NO walls, NO windows, NO vertical surfaces at all. Resembles a site plan or satellite photograph.',
      'Perspective View': 'Perspective View — camera at eye level, 30-45 degree angle to facade, showing front and one side simultaneously',
      '3D View': '3D View — elevated camera at 30-45 degrees above horizon (NOT directly overhead), showing rooftop plus facades. Three-quarter aerial perspective.',
    };
    parts.push(`SELECTED ANGLE SHOT (PRIORITY — overrides common angle): ${angleShotDescriptions[selectedAngleShot] || selectedAngleShot}`);
  }

  // Image context hints for text-to-image model
  if (hasSiteImages) {
    parts.push(
      "The first reference image(s) are SITE/LAND photographs — preserve terrain, surroundings, and atmosphere."
    );
  }

  if (hasReferenceImages) {
    parts.push(
      "The subsequent reference image(s) are BUILDING references — integrate this architecture naturally onto the site."
    );
  }

  if (hasLibraryAssets) {
    parts.push(
      "Additional reference images are provided as supplementary visual context."
    );
  }

  parts.push(
    "Photorealistic architectural site integration, hyper realism, shot on professional camera, " +
    "natural shadows and volumetric lighting, unified 3D perspective, seamless layer blending, " +
    "dimensional accuracy, realistic ground contact, atmospheric coherence, monumental building presence, " +
    "architectural photography composition."
  );

  return parts.join("\n\n");
}

/**
 * Main generation function for Flux models via BFL API
 *
 * @param {Object} params - Generation parameters
 * @param {string} params.modelName - "Flux Pro" or "Flux Flex"
 * @param {string} params.userPromptText - User's main prompt
 * @param {string} params.additionalPromptText - Additional prompt text
 * @param {Array<string>} params.moodboardImages - Array of moodboard image URLs
 * @param {Array<string>} params.referenceImages - Array of reference image URLs
 * @param {string} params.roomType - Selected room type
 * @param {string} params.selectedAngleShot - Selected camera angle
 * @param {string} params.selectedRoomSize - Selected room size
 * @param {string} params.imageSize - Aspect ratio string (e.g., "1:1", "16:9")
 * @param {AbortSignal} params.signal - Optional abort signal
 * @param {Array<string>} params.siteImages - Site/land images (site concept mode)
 * @param {Object} params.generationSettings - Generation settings: floors, lighting, buildingType, surrounding, season, sky
 * @param {string} params.toolSystemPrompt - Full system prompt for site concept
 * @param {string} params.commonAngleView - "frontal" or "topdown" (site concept mode)
 * @param {Array<string>} params.libraryAssets - Additional reference images (site concept mode)
 * @returns {Promise<string>} Base64 data URL of the generated image
 */
export async function generateWithFlux({
  modelName = "Flux Pro",
  userPromptText = "",
  additionalPromptText = "",
  directPromptText = "",
  moodboardImages = [],
  referenceImages = [],
  roomType = "",
  selectedAngleShot = "",
  selectedRoomSize = null,
  imageSize = "1:1",
  signal = null,
  // Site concept specific params
  siteImages = [],
  generationSettings = {},
  toolSystemPrompt = "",
  commonAngleView = "",
  libraryAssets = [],
}) {
  // Validate model
  const modelEndpoint = FLUX_MODELS[modelName];
  if (!modelEndpoint) {
    throw new Error(
      `Unknown Flux model: ${modelName}. Supported models: ${Object.keys(FLUX_MODELS).join(", ")}`
    );
  }

  // Detect site concept mode: site images present means we're in site concept
  const isSiteConcept = siteImages?.length > 0;

  try {
    let prompt;

    if (directPromptText?.trim()) {
      // Use the exact tool prompt provided by the caller (Gemini-aligned path)
      prompt = directPromptText.trim();
    } else if (isSiteConcept) {
      // Site Concept mode — build rich prompt with all settings (same as Gemini path)
      prompt = buildFluxSiteConceptPrompt({
        toolSystemPrompt,
        generationSettings,
        promptText: userPromptText,
        commonAngleView,
        selectedAngleShot,
        hasSiteImages: siteImages?.length > 0,
        hasReferenceImages: referenceImages?.length > 0,
        hasLibraryAssets: libraryAssets?.length > 0,
      });
    } else {
      // Standard interior design mode
      prompt = buildFluxPrompt({
        userPromptText,
        additionalPromptText,
        roomType,
        selectedAngleShot,
        selectedRoomSize,
        hasReference: referenceImages?.length > 0,
        hasMoodboard: moodboardImages?.length > 0,
      });
    }

    // Get dimensions from aspect ratio
    const { width, height } = getImageDimensions(imageSize);

    // Build BFL API request body
    const requestBody = {
      prompt,
      width,
      height,
      output_format: "png",
      safety_tolerance: 2,
    };

    // Build image list: site concept sends site→reference→library; standard sends reference→moodboard
    const allImages = isSiteConcept
      ? [...siteImages, ...referenceImages, ...(libraryAssets || [])]
      : [...referenceImages, ...moodboardImages];

    // Flux Pro supports up to 8, Flex supports up to 10
    const maxImages = modelName === "Flux Flex" ? 10 : 8;
    const imagesToSend = allImages.slice(0, maxImages);

    if (imagesToSend.length > 0) {
      // First image → input_image
      const firstImage = await imageUrlToBase64DataUri(imagesToSend[0]);
      requestBody.input_image = firstImage;

      // Additional images → input_image_2, input_image_3, ...
      for (let i = 1; i < imagesToSend.length; i++) {
        const extraImage = await imageUrlToBase64DataUri(imagesToSend[i]);
        requestBody[`input_image_${i + 1}`] = extraImage;
      }
    }

    // Step 1: Submit generation request → get polling URL
    const pollingUrl = await submitGeneration(modelEndpoint, requestBody);

    // Step 2: Poll for result
    const result = await pollForResult(pollingUrl, signal);

    // Step 3: Extract image URL and convert to base64
    const imageUrl = result?.result?.sample;

    if (imageUrl) {
      // BFL result URLs expire in 10 minutes — download immediately
      return await fetchImageAsBase64(imageUrl);
    }

    throw new Error(
      "No image was generated. Please try again with a different prompt."
    );
  } catch (error) {
    const errorInfo = handleFluxError(error);
    throw new Error(errorInfo.message);
  }
}

/**
 * Check if a model name is a Flux model
 */
export function isFluxModel(modelName) {
  return modelName in FLUX_MODELS;
}

/**
 * Get supported Flux model names
 */
export function getFluxModelNames() {
  return Object.keys(FLUX_MODELS);
}

/**
 * Check if Flux is enabled (always true — token validated server-side)
 */
export function isFluxConfigured() {
  return FLUX_ENABLED;
}
